import { DataTable } from "@/components/data-table";
import { Form } from "@/components/form";
import { formatNumber, formatString } from "@/shared/formats";
import { alpha, Stack, Typography } from "@mui/material";
import ClearIcon from '@mui/icons-material/Clear';
import useMonetizacion from "@/features/monetizacion/hooks/use-monetizacion";
import PrecioView from "@/features/monetizacion/components/precio-view";
import { useConfigurationContext } from "@/features/configuration/contexts/configuration-context";

interface VentaArticuloItem {
    articuloId: string,
    articuloNombre?: string,
    articuloCodigo?: string,
    articuloNumeroItem?: string | null,
    precioUnitario: number,
    cantidad: number,
    stockDisponible?: number
}

interface VentaArticuloListProps {
    items: VentaArticuloItem[],
    remove?: (index: number) => void
}

// Fila sin stock suficiente en el depósito seleccionado (solo si el stock es conocido).
const sinStock = (row: VentaArticuloItem) => row.stockDisponible != null && row.cantidad > row.stockDisponible;

export default function VentaArticuloList({
    items,
    remove = () => { }
}: VentaArticuloListProps) {
    const { monedaBase } = useMonetizacion();
    const { nuevaVentaItemIdentifier } = useConfigurationContext();
    const isNumItem = nuevaVentaItemIdentifier === "numItem";
    const reversedItems = [...items].reverse();

    return <Stack gap={1} flex={1} minHeight={"350px"}>
        <Stack direction={"row"} alignItems={"center"}>
            <Typography variant="h6" flex={"1 1 auto"}>Artículos</Typography>
            <Form.CheckBox name="credito" label="Crédito" size="small" />
            <Form.CheckBox name="presupuesto" label="Presupuesto" size="small" />
        </Stack>
        <DataTable flex={1}>
            <DataTable.Content
                fit="flex"
                rows={reversedItems}
                // Resaltar filas sin stock suficiente ('&&&' para ganar al zebra striping de filas impares).
                getRowSx={row => sinStock(row)
                    ? { "&&&": { backgroundColor: theme => alpha(theme.palette.error.main, 0.12) } }
                    : undefined}
                columns={[
                    { header: "#", render: (_, index) => formatNumber(index + 1), width: "auto" },
                    {
                        header: isNumItem ? "N° Ítem" : "Código",
                        render: row => formatString(isNumItem ? row.articuloNumeroItem : row.articuloCodigo),
                        width: "auto"
                    },
                    {
                        header: "Nombre", render: row => <Stack>
                            {formatString(row.articuloNombre)}
                            {sinStock(row) && <Typography variant="caption" color="error">
                                Stock insuficiente (disponible: {formatNumber(row.stockDisponible!)})
                            </Typography>}
                        </Stack>
                    },
                    { header: `Precio (${monedaBase.simbolo})`, render: row => formatNumber(row.precioUnitario), align: "right" },
                    { header: "Cantidad (uds.)", render: row => formatNumber(row.cantidad), align: "right" },
                    { header: `Subtotal (${monedaBase.simbolo})`, render: row => formatNumber(row.cantidad * row.precioUnitario), align: "right" },
                    {
                        header: "", render: (_, index) => <Form.IconButton size="small" onClick={() => remove(items.length - 1 - index)}>
                            <ClearIcon fontSize="small" />
                        </Form.IconButton>, width: "auto", align: "center"
                    }
                ]}
                footers={[
                    { render: () => <Typography variant="subtitle2">Total</Typography>, span: 3 },
                    { render: () => <Typography variant="subtitle2">{formatNumber(items.reduce((p, v) => p + v.cantidad, 0))}</Typography>, align: "right" },
                    { render: () => <PrecioView variant="h6" monto={items.reduce((p, v) => p + v.precioUnitario * v.cantidad, 0)} />, align: "right" },
                    { render: () => <></> }
                ]}
            />
        </DataTable>
    </Stack>
}